import type { EngineEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type EngineRow = EngineEvent & { pageId?: string };

export function EngineEventsTable({
  rows,
  pageIdToPosition,
  onInspect,
  warningDates,
  rowFindings,
}: {
  rows: EngineRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: EngineRow, index: number) => void;
  warningDates?: string[];
  rowFindings?: RowFindings;
}) {
  const conflictDates = new Set(warningDates ?? []);
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>Type</Th>
          <Th>Date</Th>
          <Th>MSN</Th>
          <Th>Thrust</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>A/C</Th>
          <Th>Operator</Th>
          <Th>Position</Th>
          <Th>Reason</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td>{r.type}</Td>
            <Td
              className={r.date && conflictDates.has(r.date) ? 'text-amber-700 dark:text-amber-400' : undefined}
              title={
                r.date && conflictDates.has(r.date)
                  ? 'An installation date sits before a removal at the same TSN and CSN.'
                  : undefined
              }
            >
              {r.date ?? '—'}
            </Td>
            <Td className="font-mono">{r.msn ?? '—'}</Td>
            <Td>{r.thrust}</Td>
            <Td>{r.tsn}</Td>
            <Td>{r.csn}</Td>
            <Td>{r.aircraftReg ?? '—'}</Td>
            <TruncatedTd value={r.operator} maxWidth="max-w-[200px]" />
            <Td>{r.position ?? '—'}</Td>
            <TruncatedTd value={r.reason} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}
